import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, Modal, useToast } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { useOrganizationContext } from '../../../Contexts/OrganizationContext';
import type { Organization } from '@aidkitorg/aidkit/lib/program/admin/organization';

type OrganizationModalProps = {
  isOpen: boolean;
  mode: 'create' | 'edit';
  organization: Organization | null;
  onClose: () => void;
  onSuccess: () => void;
};

export const OrganizationModal: React.FC<OrganizationModalProps> = ({
  isOpen,
  mode,
  organization,
  onClose,
  onSuccess,
}) => {
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const { createOrganization, updateOrganization } = useOrganizationContext();

  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(organization?.name ?? '');
      setIsSubmitting(false);
    }
  }, [isOpen, organization]);

  const trimmedName = name.trim();
  const isCreateMode = mode === 'create';

  const hasChanges = isCreateMode ? trimmedName.length > 0 : trimmedName !== (organization?.name?.trim() ?? '');

  const handleSubmit = useCallback(async () => {
    if (!trimmedName || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isCreateMode) {
        const result = await createOrganization({
          name: trimmedName,
          metadata: '{}',
        });

        if (result.success) {
          toast({
            variant: 'success',
            title: L.organization.organization_created_successfully,
          });
          onSuccess();
        } else {
          toast({
            variant: 'error',
            title: L.organization.failed_to_create_organization,
          });
        }
      } else if (organization) {
        if (!hasChanges) {
          onClose();
          return;
        }

        const result = await updateOrganization({
          orgUid: organization.uid,
          name: trimmedName,
        });

        if (result.success) {
          toast({
            variant: 'success',
            title: L.organization.organization_updated_successfully,
          });
          onSuccess();
        } else {
          toast({
            variant: 'error',
            title: L.organization.failed_to_update_organization,
          });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    L.organization.failed_to_create_organization,
    L.organization.failed_to_update_organization,
    L.organization.organization_created_successfully,
    L.organization.organization_updated_successfully,
    createOrganization,
    hasChanges,
    isCreateMode,
    isSubmitting,
    onClose,
    onSuccess,
    organization,
    trimmedName,
    updateOrganization,
  ]);

  const modalTitle = isCreateMode
    ? L.organization.create_new_organization
    : L.organization.edit_organization;

  return (
    <Modal open={isOpen} onClose={onClose} title={modalTitle} size="md">
      <div className="space-y-6 py-4">
        <Input
          label={L.organization.organization_name}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button
            variant="secondary"
            onClick={onClose}
            isDisabled={isSubmitting}
          >
            {L.organization.cancel}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            isDisabled={!hasChanges}
          >
            {isCreateMode ? L.organization.create_new_organization : L.organization.save_organization}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
